import { ActionReducer } from '@ngrx/store';

import {
    APP_STATE_RESET_TO_SYSTEM_DEFAULT,
    APP_STATE_RESET_TO_USER_DEFAULT,
    APP_STATE_SET_DESERIALIZED_APP_STATE,
} from './app-state.action-types';
import { LooseObject } from 'Angular/interfaces/loose-object.interface';

/*
 * Takes the `currentState` object and returns a new merged state object that updates the top level keys on the
 * `currentState` if they are defined in the `newState` object.
 *
 * Example Input:
 *      currentState = {a: 1, b: 2, c: ['one', 'two']}
 *      newState = {a: 4, c: ['three'], d: 2}
 * Example Output:
 *      mergedState = {a: 4, b: 2, c: ['three']}
 * */
const shallowMergeAppStates = (currentState: LooseObject, newState: LooseObject): LooseObject => {
    const mergedState = { ...currentState };
    Object.keys(currentState).forEach((key) => {
        if (key in newState) {
            mergedState[key] = newState[key];
        }
    });
    return mergedState;
};

/*
 * Takes the new app state that comes from the action, the default state for that app and sets the default values
 * from the app defaults when the value from the action is lacking
 * */
const validateAppState = (deserializedAppState: LooseObject, appDefaultState: LooseObject): LooseObject =>
    Object.keys(deserializedAppState).reduce(
        (validatedAppState, key) => ({
            ...validatedAppState,
            [key]: deserializedAppState[key] === false ? false : deserializedAppState[key] || appDefaultState[key],
        }),
        {}
    );

export function createReducerWithAppState(
    reducer: ActionReducer<any>,
    systemDefaultState: LooseObject,
    userDefaultState: LooseObject,
    customValidateAppState?: (deserializedAppState: LooseObject, appDefaultState: LooseObject) => LooseObject
) {
    return (state: LooseObject, action: any) => {
        if (state) {
            const sliceKey = state.appStateSliceKey;
            const currentAppStateSlice = state[sliceKey];

            if (action.type === APP_STATE_RESET_TO_USER_DEFAULT) {
                // Use the system default state if there is no user default state
                const userDefaultAppStateSlice = userDefaultState[sliceKey]
                    ? userDefaultState[sliceKey]
                    : systemDefaultState[sliceKey];
                const mergedAppState = shallowMergeAppStates(currentAppStateSlice, userDefaultAppStateSlice);

                const nextState = {
                    ...state,
                    [sliceKey]: mergedAppState,
                };

                // let any undefined value to get its default
                return reducer(nextState, { type: '@@ycharts/INIT' });
            }

            if (action.type === APP_STATE_RESET_TO_SYSTEM_DEFAULT) {
                const systemDefaultAppStateSlice = systemDefaultState[sliceKey];
                const mergedAppState = shallowMergeAppStates(currentAppStateSlice, systemDefaultAppStateSlice);

                return {
                    ...state,
                    [sliceKey]: mergedAppState,
                };
            }

            if (action.type === APP_STATE_SET_DESERIALIZED_APP_STATE) {
                const { deserializedAppState } = action;
                const defaultAppState = systemDefaultState[sliceKey];
                const validatedAppState = customValidateAppState
                    ? customValidateAppState(deserializedAppState, defaultAppState)
                    : validateAppState(deserializedAppState, defaultAppState);
                const mergedAppState = shallowMergeAppStates(currentAppStateSlice, validatedAppState);

                return {
                    ...state,
                    [sliceKey]: mergedAppState,
                };
            }
        }

        // Not one of the special actions, just call the normal reducer
        return reducer(state, action);
    };
}
